import { customerApis } from "@payping/api";
import { Delete } from "@payping/icons";
import { Checkbox, Modal, Space, Toast, Typography } from "@payping/web-components";
import { Dispatch, FC, SetStateAction } from "react";
import { useAsyncFn } from "react-use";

import { useAppTranslation } from "@/utils/useAppTranslation";
import type { ListCardProps } from "@payping/web-components/legacyComponents/List/types";

interface Props {
  retry: () => void;
  selectedRowKeys: string[];
  setSelectedRowKeys: Dispatch<SetStateAction<string[]>>;
  data: ListCardProps["dataSource"];
}

export const ListBulkActions: FC<Props> = (props) => {
  const { setSelectedRowKeys, data = [], selectedRowKeys, retry } = props;
  const { t } = useAppTranslation();
  const [modal, contextHolder] = Modal.useModal();

  const allRowsKey = data.map((row) => String(row.key));
  const isAllRowsKeySelected = allRowsKey.length === selectedRowKeys.length;

  const [{ loading: loadingPaidCustomersCodes }, getPaidCustomersCodes] = useAsyncFn(
    () => customerApis.customerCheckCodesHadPaymentPost(selectedRowKeys),
    [selectedRowKeys],
  );

  async function handleBulkAction(finalSelectedRowKeys: string[]) {
    await customerApis.customerDeletePost(finalSelectedRowKeys);
    // Clear all selections after operation
    setSelectedRowKeys([]);
    retry();
  }

  async function onBulkActionClick(): Promise<void> {
    let confirmText = t(`customer.confirm.Delete`);
    // selectedRowKeys can be updated so we need to pass the updated values to callBulkAction
    let finalSelectedRowKeys = selectedRowKeys;
    // if customer is paid it cannot be deleted
    const paidCustomersCodes = await getPaidCustomersCodes();
    if (paidCustomersCodes.length) {
      confirmText = t("customer.confirm.deleteSome", { codes: paidCustomersCodes.join(", ") });
      const notPaidCustomers = selectedRowKeys.filter(
        (selectedCode) => !paidCustomersCodes.find((paidCustomerCode) => paidCustomerCode === selectedCode),
      );
      finalSelectedRowKeys = notPaidCustomers;
      setSelectedRowKeys(notPaidCustomers);
    }
    // if there is anything to delete
    if (finalSelectedRowKeys.length) {
      modal.confirm({
        cancelText: t("cancel"),
        content: confirmText,
        mask: { closable: true },
        okText: t("ok"),
        onOk: async () => handleBulkAction(finalSelectedRowKeys),
      });
    } else {
      modal.error({ content: t("customer.confirm.deleteNone") });
    }
  }

  return (
    <Toast visible={Boolean(selectedRowKeys.length)} loading={loadingPaidCustomersCodes}>
      <Toast.Item>
        <Space size={"small"}>
          <Checkbox
            onClick={() => {
              if (isAllRowsKeySelected) {
                setSelectedRowKeys([]);
                return;
              }
              setSelectedRowKeys(allRowsKey);
            }}
            checked={isAllRowsKeySelected}
            indeterminate={!isAllRowsKeySelected && selectedRowKeys.length > 0}
          >
            <Typography.Text type={"secondary"}>{t("common.operation.selectAll")}</Typography.Text>
          </Checkbox>
          <Typography.Text type={"secondary"}>{`${selectedRowKeys.length} ${t(
            "common.selectedItem",
          )}`}</Typography.Text>
        </Space>
      </Toast.Item>
      <Toast.Item
        label={t("common.operation.delete")}
        Icon={() => <Delete width={16} />}
        onClick={() => onBulkActionClick()}
      />
      {contextHolder}
    </Toast>
  );
};
